<i18n>
  {
    "fr": {
      "itinerary-summary": "Résumé de l'itinéraire",
      "outward-flight": "Aller",
      "return-flight": "Retour",
      "lands-on": "Atterrit le",
      "terminal": "Terminal",
      "stopover-details": "Changement d'avion à",
      "stopover-airport-change": "Attention, changement d'aéroport de",
      "stopover-to": "à",
      "ticket-remb": "Billets remboursable ou échangeable sous conditions",
      "ticket-no-remb": "Billets non remboursables",
      "info-supplier-class": "Information fournie par le fournisseur dans la langue de son choix."
    },
    "en": {
      "itinerary-summary": "Itinerary summary",
      "outward-flight": "Outward flight",
      "return-flight": "Return flight",
      "lands-on": "Lands on",
      "terminal": "Terminal",
      "stopover-details": "Change of plane in",
      "stopover-airport-change": "Attention, change of airport from",
      "stopover-to": "to",
      "ticket-remb": "Ticket refundable or exchangeable on conditions",
      "ticket-no-remb": "Non-refundable ticket",
      "info-supplier-class": "Information provided by the supplier in the language of their choice."
    }
  }
</i18n>

<template>
  <v-card flat>
    <v-card-title>
      <h1 class="text--primary title">
        {{ $t('itinerary-summary') }}
      </h1>
    </v-card-title>

    <v-card-text>
      <v-row class="mt-4">
        <v-col
          cols="2"
          align-self="center"
        >
          <div class="my-auto text-center">
            <h3 class="mb-0 text--primary">
              <v-icon
                color="accent"
                class="mr-2"
              >
                mdi-airplane mdi-rotate-90
              </v-icon>
              <span
                class="font-weight-bold heading mx-auto"
              >
                {{ $t('outward-flight') }}
              </span>
            </h3>
          </div>
        </v-col>
        <v-col>
          <div
            id="outward-flight-summary"
            class="flight-summary px-4"
          >
            <div
              v-for="(segment, index) in flightOutward.segments"
              :key="index"
            >
              <v-row>
                <v-col
                  cols="3"
                  class="px-4"
                >
                  <p class="my-0 subtitle-2">
                    {{ formatDate(segment.begin) }}
                  </p>
                  <p
                    v-if="arrivalIsNotTheSameDay(segment)"
                    class="caption red--text"
                  >
                    {{ $t('lands-on') }} {{ formatDate(segment.end) }}
                  </p>
                </v-col>
                <v-col>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        small
                        class="pr-2"
                      >
                        mdi-clock-outline
                      </v-icon>
                      <b>{{ formatHour(segment.begin) }} - {{ formatHour(segment.end) }}</b>
                    </v-col>
                    <v-spacer />
                    <v-col
                      cols="4"
                      class="pr-4 text-right"
                    >
                      <div>
                        <b>{{ formatDuration(segment.duration) }}</b>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        small
                        class="mr-2"
                      >
                        mdi-airplane-takeoff
                      </v-icon>
                      <span>{{ segment.from_place.name }} (<b>{{ segment.from_place.code }}</b>)</span>
                      <span v-if="segment.from_place.terminal">
                        <span> - </span>
                        <span>{{ $t('terminal') }} {{ segment.from_place.terminal }}</span>
                      </span>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        small
                        class="mr-2"
                      >
                        mdi-airplane-landing
                      </v-icon>
                      <span>{{ segment.to_place.name }} (<b>{{ segment.to_place.code }}</b>)</span>
                      <span v-if="segment.to_place.terminal">
                        <span> - </span>
                        <span>{{ $t('terminal') }} {{ segment.to_place.terminal }}</span>
                      </span>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        small
                        class="mr-2"
                      >
                        mdi-credit-card-outline
                      </v-icon>
                      <span>{{ segment.marketing_carrier_name }} (<b>{{ segment.marketing_carrier }} {{ segment.flight_number }}</b>)</span>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        class="mr-2"
                        small
                      >
                        mdi-credit-card-refund
                      </v-icon>
                      <span v-if="itinerary.price.passengers_prices[0].refundable === 'true'">{{ $t('ticket-remb') }}</span>
                      <span v-else>{{ $t('ticket-no-remb') }}</span>
                    </v-col>
                    <v-col
                      v-if="segment.supplier_class_description"
                      class="text-left"
                    >
                      <v-tooltip top>
                        <template #activator="{on, attrs}">
                          <v-icon
                            v-bind="attrs"
                            class="mr-2"
                            small
                            v-on="on"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>{{ $t('info-supplier-class') }}</span>
                      </v-tooltip>
                      <span>{{ segment.supplier_class_description }}</span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row
                v-if="index < (flightOutward.segments.length - 1)"
                no-gutters
              >
                <v-col>
                  <v-card
                    flat
                    outlined
                    color="primaryLight"
                    class="pa-0"
                  >
                    <v-row>
                      <v-col
                        cols="10"
                        class="text-left"
                      >
                        <template v-if="!stopoverAirportChange(flightOutward.segments[index], flightOutward.segments[index + 1])">
                          <span class="pl-2">{{ $t('stopover-details') }} </span>
                          <span class="subtitle-2">{{ flightOutward.segments[index].to_place.name }} ({{ flightOutward.segments[index].to_place.code }})</span>
                        </template>
                        <template v-if="stopoverAirportChange(flightOutward.segments[index], flightOutward.segments[index + 1])">
                          <p class="font-weight-bold my-0 pl-2 red--text">
                            {{ displayAirportChange(flightOutward.segments[index], flightOutward.segments[index + 1]) }}
                          </p>
                        </template>
                      </v-col>
                      <v-col class="mr-2 text-right">
                        <p class="my-0">
                          <b>{{ stopoverDuration(flightOutward.segments[index], flightOutward.segments[index + 1]) }}</b>
                        </p>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row class="mt-8">
        <v-col
          v-if="flightReturn"
          cols="2"
          align-self="center"
        >
          <div class="my-auto text-center">
            <h3 class="mb-0 text--primary">
              <v-icon
                color="accent"
                class="mr-2"
              >
                mdi-airplane mdi-rotate-270
              </v-icon>
              <span
                class="font-weight-bold heading mx-auto"
              >
                {{ $t('return-flight') }}
              </span>
            </h3>
          </div>
        </v-col>
        <v-col>
          <div
            v-if="flightReturn"
            id="return-flight-summary"
            class="flight-summary my-2 px-4"
          >
            <div
              v-for="(segment, index) in flightReturn.segments"
              :key="index"
            >
              <v-row>
                <v-col
                  cols="3"
                  class="px-4"
                >
                  <p class="my-0 subtitle-2">
                    {{ formatDate(segment.begin) }}
                  </p>
                  <p
                    v-if="arrivalIsNotTheSameDay(segment)"
                    class="caption red--text"
                  >
                    {{ $t('lands-on') }} {{ formatDate(segment.end) }}
                  </p>
                </v-col>
                <v-col>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        small
                        class="pr-2"
                      >
                        mdi-clock-outline
                      </v-icon>
                      <b>{{ formatHour(segment.begin) }} - {{ formatHour(segment.end) }}</b>
                    </v-col>
                    <v-spacer />
                    <v-col
                      cols="4"
                      class="pr-4 text-right"
                    >
                      <div>
                        <b>{{ formatDuration(segment.duration) }}</b>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        small
                        class="mr-2"
                      >
                        mdi-airplane-takeoff
                      </v-icon>
                      <span>{{ segment.from_place.name }} (<b>{{ segment.from_place.code }}</b>)</span>
                      <span v-if="segment.from_place.terminal">
                        <span> - </span>
                        <span>{{ $t('terminal') }} {{ segment.from_place.terminal }}</span>
                      </span>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        small
                        class="mr-2"
                      >
                        mdi-airplane-landing
                      </v-icon>
                      <span>{{ segment.to_place.name }} (<b>{{ segment.to_place.code }}</b>)</span>
                      <span v-if="segment.to_place.terminal">
                        <span> - </span>
                        <span>{{ $t('terminal') }} {{ segment.to_place.terminal }}</span>
                      </span>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        small
                        class="mr-2"
                      >
                        mdi-credit-card-outline
                      </v-icon>
                      <span>{{ segment.marketing_carrier_name }} (<b>{{ segment.marketing_carrier }} {{ segment.flight_number }}</b>)</span>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="text-left">
                      <v-icon
                        class="mr-2"
                        small
                      >
                        mdi-credit-card-refund
                      </v-icon>
                      <span v-if="itinerary.price.passengers_prices[0].refundable === 'true'">{{ $t('ticket-remb') }}</span>
                      <span v-else>{{ $t('ticket-no-remb') }}</span>
                    </v-col>
                    <v-col
                      v-if="segment.supplier_class_description"
                      class="text-left"
                    >
                      <v-tooltip top>
                        <template #activator="{on, attrs}">
                          <v-icon
                            v-bind="attrs"
                            class="mr-2"
                            small
                            v-on="on"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span>{{ $t('info-supplier-class') }}</span>
                      </v-tooltip>
                      <span>{{ segment.supplier_class_description }}</span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row
                v-if="index < (flightReturn.segments.length - 1)"
                no-gutters
              >
                <v-col>
                  <v-card
                    flat
                    outlined
                    color="primaryLight"
                    class="pa-0"
                  >
                    <v-row>
                      <v-col
                        cols="10"
                        class="text-left"
                      >
                        <template v-if="!stopoverAirportChange(flightReturn.segments[index], flightReturn.segments[index + 1])">
                          <span class="pl-2">{{ $t('stopover-details') }} </span>
                          <span class="subtitle-2">{{ flightReturn.segments[index].to_place.name }} ({{ flightReturn.segments[index].to_place.code }})</span>
                        </template>
                        <template v-if="stopoverAirportChange(flightReturn.segments[index], flightReturn.segments[index + 1])">
                          <p class="font-weight-bold my-0 pl-2 red--text">
                            {{ displayAirportChange(flightReturn.segments[index], flightReturn.segments[index + 1]) }}
                          </p>
                        </template>
                      </v-col>
                      <v-col class="mr-2 text-right">
                        <p class="my-0">
                          <b>{{ stopoverDuration(flightReturn.segments[index], flightReturn.segments[index + 1]) }}</b>
                        </p>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'FlightItinerarySummary',
  computed: {
    ...mapState({
      itinerary: state => state.fb.searchResults.updatedSelectedItinerary
    }),
    /**
     * Return the outward flight object from the updated selected itinerary from the vueX store
     * @return {Object}
     */
    flightOutward () {
      return this.itinerary.flight_outward
    },
    /**
     * Return the return flight object from the updated selected itinerary from the vueX store
     * @return {Object}
     */
    flightReturn () {
      return this.itinerary.flight_return
    }
  },
  methods: {
    /**
     * Format duration to a HH'H'mm'm' format
     * @param durationInMinutes
     * @return {string}
     */
    formatDuration (durationInMinutes) {
      const momentDuration = this.$moment.duration(durationInMinutes, 'minutes')
      const hours = Math.floor(momentDuration.asHours())
      let minutes = (momentDuration.minutes())
      if (minutes < 10) {
        const m = minutes.toString()
        minutes = `0${m}`
      }
      return `${hours}H${minutes}m`
    },
    /**
     * Take a datetime object and return a string with HH:mm format
     * @param datetime {Object}
     * @return {*}
     */
    formatHour (datetime) {
      // console.log('formatHour : ', datetime)
      if (!datetime.endsWith('Z')) {
        datetime += 'Z'
      }
      // console.log('           : ', datetime)
      const momentHour = this.$moment(datetime)
      return momentHour.utc().format('HH:mm')
    },
    /**
     * Take a datetime object and return a string with dddd DD MMMM format
     * @param datetime {Object}
     * @return {*}
     */
    formatDate (datetime) {
      const formatDate = this.$moment(datetime)
      return formatDate.utc().format('dddd DD MMMM')
    },
    /**
     * Take a segment object from an itinerary object and return a boolean if the
     * arrival of the itinerary is not the same day as the departure
     * @param segment {Object}
     * @return {boolean}
     */
    arrivalIsNotTheSameDay (segment) {
      const departure = this.$moment(segment.begin)
      const arrival = this.$moment(segment.end)
      return departure.utc().format('DD') !== arrival.utc().format('DD')
    },
    /**
     * Take 2 segments as an input and return a duration as a string.
     * It calculate the stopover duration between 2 segments
     * @param segment1 {Object}
     * @param segment2 {Object}
     * @return {string}
     */
    stopoverDuration (segment1, segment2) {
      const dateTime1 = this.$moment(segment1.end)
      const dateTime2 = this.$moment(segment2.begin)
      const diff = dateTime2.diff(dateTime1, 'minutes')
      return this.formatDuration(diff)
    },
    /**
     * Take 2 segments as an input and return true if there is a change of airport during a stopover.
     * @param segment1 {Object}
     * @param segment2 {Object}
     * @return {boolean}
     */
    stopoverAirportChange (segment1, segment2) {
      return segment1.to_place.code !== segment2.from_place.code
    },
    /**
     * Construct a sentence to display the airport change during a stopover.
     * @param segment1 {Object}
     * @param segment2 {Object}
     * @return {string}
     */
    displayAirportChange (segment1, segment2) {
      const begin = this.$i18n.t('stopover-airport-change')
      const to = this.$i18n.t('stopover-to')
      const departureAirport = `${segment1.to_place.name} (${segment1.to_place.code})`
      const arrivalAirport = `${segment2.from_place.name} (${segment2.from_place.code})`
      return `${begin} ${departureAirport} ${to} ${arrivalAirport}`
    }
  }
}
</script>

<style scoped>
    .flight-summary {
      border-left: solid 1px rgba(0, 61, 88, 0.30);
    }
    .warning-icon {
      top: calc(50% - 12px);
      left: calc(50% - 12px);
    }
</style>
